import React, { useState } from 'react';
import { Link, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { LineChart, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../features/auth/AuthContext';
import { formatApiErrorDetail } from '../../services/api/client';
import { resendVerification } from '../../services/api/auth';
import { BrandLogo } from '../../components/common/BrandLogo';
import { LOGIN } from '../../constants/testIds';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '';
  const safeNext = next.startsWith('/') ? next : '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);

  if (user) {
    const home = user.roles.includes('admin') ? '/admin/dashboard' : '/member/dashboard';
    return <Navigate to={safeNext || home} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await login(email, password);
      const home = u.roles.includes('admin') ? '/admin/dashboard' : '/member/dashboard';
      navigate(safeNext || home, { replace: true });
    } catch (err: any) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(detail);
      setNeedsVerify(err.response?.status === 403 && /verifikasi/i.test(detail));
    } finally {
      setLoading(false);
    }
  };

  const doResend = async () => {
    try { await resendVerification(email); toast.success('Email verifikasi dikirim ulang', { description: 'Cek kotak masuk Anda.' }); }
    catch { toast.success('Jika email terdaftar, tautan verifikasi telah dikirim.'); }
  };

  return (
    <div className="min-h-screen bg-[#050914] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute w-[800px] h-[800px] bg-cyan-500/10 blur-[120px] rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="w-full max-w-md bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-3xl p-8 sm:p-12 relative z-10 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="mb-8">
          <BrandLogo />
        </div>

        <h1 className="text-3xl tracking-tight font-light text-white mb-2">Selamat datang kembali</h1>
        <p className="text-white/50 text-sm mb-8">Masuk ke akun Anda untuk melanjutkan.</p>

        {safeNext && (
          <p data-testid="login-share-notice" className="text-sm text-cyan-300/80 bg-cyan-400/10 border border-cyan-400/20 rounded-lg px-4 py-2.5 mb-6">
            Masuk atau daftar dulu untuk membuka materi yang dibagikan kepada Anda.
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="block text-sm text-white/70 mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={LOGIN.emailInput}
              placeholder="you@email.com"
              className="w-full bg-[#090D1A] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={LOGIN.passwordInput}
                placeholder="••••••••"
                className="w-full bg-[#090D1A] border border-white/10 rounded-lg px-4 py-2.5 pr-11 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors"
              />
              <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-cyan-400 transition-colors" aria-label={showPw ? 'Sembunyikan password' : 'Tampilkan password'}>
                {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.8} /> : <Eye className="w-4 h-4" strokeWidth={1.8} />}
              </button>
            </div>
          </div>

          <div className="flex justify-end -mt-2">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-cyan-400 hover:text-cyan-300">
              Lupa password?
            </Link>
          </div>

          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5" data-testid="login-error">
              {error}
            </p>
          )}

          {needsVerify && (
            <button type="button" data-testid="resend-verification-link" onClick={doResend} className="text-xs text-cyan-400 hover:text-cyan-300 underline">
              Kirim ulang email verifikasi
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid={LOGIN.submitButton}
            className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
            Masuk / Login
          </button>
        </form>

        <p className="text-sm text-white/50 mt-8 text-center">
          Belum punya akun?{' '}
          <Link to={safeNext ? `/register?next=${encodeURIComponent(safeNext)}` : '/register'} data-testid={LOGIN.registerLink} className="text-cyan-400 hover:text-cyan-300">
            Daftar sekarang
          </Link>
        </p>
      </div>
    </div>
  );
}
